import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { syncAccountBalance } from '../lib/accountBalance';
import { logAudit } from '../lib/audit';
import { log, logError, startTimer } from '../lib/logger';
import { trackMetric } from '../lib/metrics';

export type CardBrand = 'visa' | 'mastercard' | 'elo' | 'amex' | 'hipercard' | 'outro';
export type CardCurrency = 'BRL' | 'USD' | 'EUR';
export type CardPaymentMethod = 'manual' | 'automatic_debit' | 'external';
export type CardType = 'credit' | 'debit' | 'multiple';

export interface Card {
  id: string;
  user_id: string;
  name: string;
  nickname: string;
  bank: string;
  bank_name: string;
  bank_code: string;
  brand: CardBrand;
  last_four_digits: string;
  limit_amount: number;
  currency: CardCurrency;
  closing_day: number;
  due_day: number;
  color: string;
  is_active: boolean;
  is_archived: boolean;
  notes: string;
  linked_account_id: string | null;
  payment_method: CardPaymentMethod;
  card_type: CardType;
  created_at: string;
  updated_at: string;
}

export interface CardTransaction {
  id: string;
  user_id: string;
  card_id: string;
  description: string;
  category: string;
  amount: number;
  date: string;
  installments: number;
  current_installment: number;
  status: 'pago' | 'pendente';
  created_at: string;
}

export interface CardStats {
  usedLimit: number;
  availableLimit: number;
  totalPaid: number;
  pendingAmount: number;       // currentInvoice - totalPaid (how much is still owed this cycle)
  currentInvoice: number;
  nextInvoice: number;
  daysToClose: number;
  daysToDue: number;
  currentCycleStart: string;
  currentCycleEnd: string;
  currentCycleTransactions: CardTransaction[];
  allRecentTransactions: CardTransaction[];
}

/** Returns start and end dates of the current billing cycle */
export const getCurrentBillingCycle = (closingDay: number) => {
  const today = new Date();
  const d = today.getDate();
  const y = today.getFullYear();
  const m = today.getMonth();

  let start: Date;
  let end: Date;

  if (d < closingDay) {
    start = new Date(y, m - 1, closingDay);
    end = new Date(y, m, closingDay - 1);
  } else {
    start = new Date(y, m, closingDay);
    end = new Date(y, m + 1, closingDay - 1);
  }
  return { start, end };
};

export const useCards = (includeArchived = false) => {
  const { user } = useAuth();
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Security: clear stale data immediately when user changes or logs out ──
  const prevUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== currentUserId) {
      if (prevUserIdRef.current !== undefined || currentUserId === undefined) {
        setCards([]);
        setError(null);
      }
      prevUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  const fetchCards = useCallback(async () => {
    if (!user) {
      setCards([]);   // Clear stale data on logout
      setLoading(false);
      return;
    }
    setLoading(true);
    let query = supabase
      .from('cards')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true });

    if (!includeArchived) query = query.eq('is_archived', false);

    const { data, error: err } = await query;
    if (err) setError(err.message);
    else setCards((data as Card[]) ?? []);
    setLoading(false);
  }, [user, includeArchived]);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const createCard = async (values: Omit<Card, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return { error: 'Não autenticado' };
    const { error: err } = await supabase.from('cards').insert({ ...values, user_id: user.id });
    if (!err) await fetchCards();
    return { error: err?.message ?? null };
  };

  const updateCard = async (id: string, values: Partial<Card>) => {
    const { error: err } = await supabase
      .from('cards').update({ ...values, updated_at: new Date().toISOString() })
      .eq('id', id).eq('user_id', user?.id ?? '');
    if (!err) await fetchCards();
    return { error: err?.message ?? null };
  };

  const archiveCard = async (id: string) => {
    const { error: err } = await supabase
      .from('cards').update({ is_archived: true, is_active: false, updated_at: new Date().toISOString() })
      .eq('id', id).eq('user_id', user?.id ?? '');
    if (!err) {
      // Soft-delete transações pendentes vinculadas ao cartão para evitar que apareçam
      // no dashboard como pendências órfãs — consistente com o comportamento de archiveAccount.
      await supabase
        .from('transactions')
        .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq('card_id', id)
        .eq('user_id', user?.id ?? '')
        .in('status', ['pendente', 'vencido', 'atrasado'])
        .is('deleted_at', null);
      await fetchCards();
    }
    return { error: err?.message ?? null };
  };

  const unarchiveCard = async (id: string) => {
    const { error: err } = await supabase
      .from('cards').update({ is_archived: false, is_active: true, updated_at: new Date().toISOString() })
      .eq('id', id).eq('user_id', user?.id ?? '');
    if (!err) await fetchCards();
    return { error: err?.message ?? null };
  };

  const deleteCard = async (id: string): Promise<{ error: string | null; hasTransactions?: boolean }> => {
    // CRÍTICO: filtrar deleted_at IS NULL para não bloquear exclusão por registros já soft-deletados
    const { count: txCount } = await supabase
      .from('transactions').select('id', { count: 'exact', head: true })
      .eq('card_id', id).eq('user_id', user?.id ?? '')
      .is('deleted_at', null);

    const { count: cardTxCount } = await supabase
      .from('card_transactions').select('id', { count: 'exact', head: true })
      .eq('card_id', id).eq('user_id', user?.id ?? '');

    if ((txCount ?? 0) > 0 || (cardTxCount ?? 0) > 0) {
      return { error: null, hasTransactions: true };
    }
    const { error: err } = await supabase.from('cards').delete()
      .eq('id', id).eq('user_id', user?.id ?? '');
    if (!err) await fetchCards();
    return { error: err?.message ?? null, hasTransactions: false };
  };

  const getCardStats = useCallback(async (card: Card): Promise<CardStats> => {
    const { data: allTxs } = await supabase
      .from('transactions')
      .select('id, description, category, amount, date, status, installments, installment_number')
      .eq('card_id', card.id)
      .eq('user_id', user?.id ?? '')
      .eq('type', 'despesa')
      .is('deleted_at', null) // ← CRÍTICO: excluir soft-deleted da fatura e limite
      .order('date', { ascending: false });

    const txs = (allTxs ?? []).map((t: any) => ({
      ...t,
      current_installment: t.installment_number ?? 1,
    })) as CardTransaction[];

    const fmtDate = (d: Date) => d.toISOString().split('T')[0];
    const { start, end } = getCurrentBillingCycle(card.closing_day);
    const cycleStart = fmtDate(start);
    const cycleEnd = fmtDate(end);

    // ── Current cycle transactions ────────────────────────────────────────────
    const inCurrent = txs.filter(t => t.date >= cycleStart && t.date <= cycleEnd);
    const currentInvoice = inCurrent.reduce((s, t) => s + Number(t.amount), 0);

    // ── Next cycle transactions ───────────────────────────────────────────────
    const nextStart = new Date(end);
    nextStart.setDate(nextStart.getDate() + 1);
    const nextEnd = new Date(nextStart);
    nextEnd.setMonth(nextEnd.getMonth() + 1);
    const inNext = txs.filter(t => t.date >= fmtDate(nextStart) && t.date <= fmtDate(nextEnd));
    const nextInvoice = inNext.reduce((s, t) => s + Number(t.amount), 0);

    // ── Used limit: ALL unpaid transactions in current + future cycles ────────
    // Only count transactions from current cycle start forward to determine credit usage
    const openTxs = txs.filter(t => t.date >= cycleStart);
    const usedLimit = openTxs.reduce((s, t) => s + Number(t.amount), 0);

    // ── Payments for this cycle ───────────────────────────────────────────────
    // Fetch payments where billing_cycle_start matches OR payment_date >= cycleStart
    const { data: payments } = await supabase
      .from('card_payments')
      .select('amount, payment_date, billing_cycle_start')
      .eq('card_id', card.id)
      .eq('user_id', user?.id ?? '')
      .gte('payment_date', cycleStart);

    const totalPaid = (payments ?? []).reduce((s: number, p: any) => s + Number(p.amount), 0);

    // ── Pending: what still needs to be paid on this cycle's invoice ──────────
    const pendingAmount = Math.max(0, currentInvoice - totalPaid);

    // ── Available limit ───────────────────────────────────────────────────────
    // availableLimit = total limit - credit in use + what's been paid back
    const availableLimit = Math.max(0, card.limit_amount - usedLimit + totalPaid);

    // ── Days to close / due ───────────────────────────────────────────────────
    const today = new Date();
    const day = today.getDate();
    // Real days remaining in the current month (handles 28/29/30/31)
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const daysToClose = card.closing_day >= day
      ? card.closing_day - day
      : daysInMonth - day + card.closing_day;
    const daysToDue = card.due_day >= day
      ? card.due_day - day
      : daysInMonth - day + card.due_day;

    return {
      usedLimit, availableLimit, totalPaid, pendingAmount,
      currentInvoice, nextInvoice,
      daysToClose, daysToDue,
      currentCycleStart: cycleStart,
      currentCycleEnd: cycleEnd,
      currentCycleTransactions: inCurrent,
      allRecentTransactions: txs.slice(0, 30),
    };
  }, [user]);

  const payCardInvoice = useCallback(async (params: {
    cardId: string;
    amount: number;
    paymentDate: string;
    paymentMethod: string;
    sourceAccountId: string | null;
    notes: string;
    affectsAccountBalance: boolean;
    billingCycleStart?: string;
  }): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Usuário não autenticado.' };
    const elapsed = startTimer();
    log({ flow: 'payCardInvoice', status: 'start', userId: user.id, entity: 'card_payment', recordId: params.cardId, context: { amount: params.amount, paymentDate: params.paymentDate } });

    // 1. Record payment in card_payments
    const { error: payErr } = await supabase.from('card_payments').insert({
      user_id: user.id,
      card_id: params.cardId,
      amount: params.amount,
      payment_date: params.paymentDate,
      source_account_id: params.sourceAccountId,
      payment_method: params.paymentMethod,
      notes: params.notes || null,
      affects_account_balance: params.affectsAccountBalance,
      billing_cycle_start: params.billingCycleStart ?? null,
    });

    if (payErr) {
      logError('payCardInvoice', `Erro ao registrar pagamento: ${payErr.message}`, { userId: user.id, entity: 'card_payment', recordId: params.cardId, context: { code: payErr.code }, durationMs: elapsed() });
      trackMetric('pay_card_invoice', { flow: 'payCardInvoice', success: false, durationMs: elapsed() });
      return { success: false, message: `Erro ao registrar pagamento: ${payErr.message}` };
    }

    // 2. If affects account balance, create despesa transaction in account
    if (params.affectsAccountBalance && params.sourceAccountId) {
      const card = cards.find(c => c.id === params.cardId);
      const cardName = card?.name ?? 'Cartão';

      const { error: txErr } = await supabase.from('transactions').insert({
        user_id: user.id,
        account_id: params.sourceAccountId,
        card_id: null,
        payment_method: 'account',
        description: `Pagamento fatura — ${cardName}`,
        category: 'Cartões',
        type: 'despesa',
        amount: params.amount,
        date: params.paymentDate,
        status: 'pago',
        notes: params.notes ?? '',
        recurrence: 'none',
        installments: 1,
        installment_number: 1,
        installment_group_id: null,
      });

      if (txErr) {
        logError('payCardInvoice', 'Falha ao criar transação na conta vinculada', { userId: user.id, entity: 'transaction', context: { code: txErr.code } });
      } else {
        await syncAccountBalance(params.sourceAccountId);
      }
    }

    // 3. Mark all pending card transactions in the billing cycle as 'pago'
    //    This ensures pendingExpenses is recalculated correctly in the dashboard
    if (params.billingCycleStart) {
      const card = cards.find(c => c.id === params.cardId);
      const closingDay = card?.closing_day ?? 1;

      // Cycle end = day before closing_day of the month following cycleStart
      const cycleStartDate = new Date(params.billingCycleStart + 'T00:00:00');
      const cycleEndDate = new Date(
        cycleStartDate.getFullYear(),
        cycleStartDate.getMonth() + 1,
        closingDay - 1,
      );
      const cycleEndStr = cycleEndDate.toISOString().split('T')[0];

      const { error: markErr } = await supabase
        .from('transactions')
        .update({ status: 'pago', updated_at: new Date().toISOString() })
        .eq('card_id', params.cardId)
        .eq('user_id', user.id)
        .eq('type', 'despesa')
        .in('status', ['pendente', 'atrasado', 'vencido'])
        .is('deleted_at', null)
        .gte('date', params.billingCycleStart)
        .lte('date', cycleEndStr);

      if (markErr) {
        // Non-fatal: payment was already recorded; log for monitoring
        logError('payCardInvoice', 'Falha ao marcar transações do ciclo como pagas (não fatal)', { userId: user.id, entity: 'card_payment', recordId: params.cardId, context: { code: markErr.code } });
      }
    }

    // ── Audit trail: registrar pagamento de fatura ─────────────────────────
    logAudit({
      action: 'pay_card_invoice',
      resource: 'card_payment',
      resource_id: params.cardId,
      details: {
        amount: params.amount,
        paymentDate: params.paymentDate,
        paymentMethod: params.paymentMethod,
        sourceAccountId: params.sourceAccountId,
        billingCycleStart: params.billingCycleStart,
      },
    });

    log({ flow: 'payCardInvoice', status: 'success', userId: user.id, entity: 'card_payment', recordId: params.cardId, message: 'Pagamento de fatura registrado', context: { amount: params.amount, method: params.paymentMethod }, durationMs: elapsed() });
    trackMetric('pay_card_invoice', { flow: 'payCardInvoice', success: true, durationMs: elapsed() });
    return { success: true, message: 'Pagamento registrado com sucesso! O limite foi liberado.' };
  }, [user, cards]);

  return {
    cards, loading, error,
    createCard, updateCard, archiveCard, unarchiveCard, deleteCard,
    getCardStats, payCardInvoice, refetch: fetchCards,
  };
};

export const useCardTransactions = (cardId?: string) => {
  const { user } = useAuth();
  const [cardTransactions, setCardTransactions] = useState<CardTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCardTransactions = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    let query = supabase.from('card_transactions').select('*')
      .eq('user_id', user.id).order('date', { ascending: false });
    if (cardId) query = query.eq('card_id', cardId);
    const { data } = await query;
    setCardTransactions((data as CardTransaction[]) ?? []);
    setLoading(false);
  }, [user, cardId]);

  useEffect(() => { fetchCardTransactions(); }, [fetchCardTransactions]);

  const createCardTransaction = async (values: Omit<CardTransaction, 'id' | 'user_id' | 'created_at'>) => {
    if (!user) return;
    const { error: err } = await supabase.from('card_transactions').insert({ ...values, user_id: user.id });
    if (!err) await fetchCardTransactions();
    return err;
  };

  const deleteCardTransaction = async (id: string) => {
    const { error: err } = await supabase.from('card_transactions').delete().eq('id', id);
    if (!err) await fetchCardTransactions();
    return err;
  };

  return { cardTransactions, loading, createCardTransaction, deleteCardTransaction, refetch: fetchCardTransactions };
};
